import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import {
  buildSizeChartTemplatePayload,
  validateSizeChartForm,
} from '@/temu-platform/utils/temuSizeChart';
import {
  buildOfficialSizeChartTemplatePayload,
  saveOfficialSizeChartAsReusableTemplate,
  validateOfficialSizeChartDraft,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import { nextTemplatePriority } from '@/temu-platform/utils/temuTemplatePriority';
import { temuPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

/**
 * 另存尺码库：元信息弹窗（字段/样式对齐属性库保存弹窗）。
 * - 官方尺码表路径：传入 officialSizeChart + sizeClass 等
 * - 旧矩阵路径：传入 sizeChart / chartRows / measureAttrs
 */
export default function SaveSizeChartTemplateMetaModal({
  open,
  shopId,
  officialMode = false,
  shopType = '',
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  sizeSaleAttributeId = '',
  sizeChart = {},
  chartRows = [],
  measureAttrs = [],
  sizeAttr = null,
  selectedSizeIds = [],
  selectedSizeLabels = [],
  skcList = [],
  /** 官方尺码表草稿（TemuOfficialSizeChartPanel） */
  officialSizeChart = null,
  sizeClass = null,
  /** 打开时预填款号（如发品页已有款号） */
  defaultStyleNo = '',
  onClose,
  onSaved,
}) {
  useTranslation('workbench');
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  const isOfficialChart = Boolean(
    officialSizeChart
    && typeof officialSizeChart === 'object'
    && (Array.isArray(officialSizeChart.records) || officialSizeChart.mode),
  );

  useEffect(() => {
    if (!open) return;
    const labels = (Array.isArray(selectedSizeLabels) ? selectedSizeLabels : [])
      .map((n) => String(n || '').trim())
      .filter(Boolean);
    setName(labels.length ? `尺码表-${labels.join('/')}` : '');
    setStyleNo(String(defaultStyleNo || '').trim());
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setSaving(false);
    // 仅在弹窗打开时预填，避免输入过程中被重置
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open 边沿初始化
  }, [open]);

  useEffect(() => {
    if (!open || !productTypeId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setPriorityLoading(true);
    const load = officialMode
      ? adminAPI.listOfficialTemuSizeChartTemplates({
        productTypeId,
        shopType,
        page: 1,
        pageSize: 100,
      })
      : temuPlatformAPI.listSizeChartTemplates(shopId, {
        productTypeId,
        page: 1,
        pageSize: 100,
      });
    load
      .then((res) => {
        if (cancelled) return;
        setSortOrder(nextTemplatePriority(res?.items));
      })
      .catch(() => {
        if (!cancelled) setSortOrder(0);
      })
      .finally(() => {
        if (!cancelled) setPriorityLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, productTypeId, officialMode, shopType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(chartText('saveTpl.needStyle', fixedUi("请填写款号")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('saveTpl.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    if (!categoryId || !productTypeId) {
      showToast(chartText('saveTpl.needCategory', fixedUi("缺少适用类目")), 'error');
      return;
    }

    setSaving(true);
    try {
      let payload;
      if (isOfficialChart) {
        let draft = {
          mode: officialSizeChart.mode || 'create',
          sizeClass: sizeClass || officialSizeChart.sizeClass || null,
          selectedBusinessIds: Array.isArray(officialSizeChart.selectedBusinessIds)
            ? officialSizeChart.selectedBusinessIds
            : [],
          selectedLibraryId: String(officialSizeChart.selectedLibraryId || '').trim(),
          records: Array.isArray(officialSizeChart.records) ? officialSizeChart.records : [],
          groupList: Array.isArray(officialSizeChart.groupList) ? officialSizeChart.groupList : [],
          elementList: Array.isArray(officialSizeChart.elementList) ? officialSizeChart.elementList : [],
        };
        const sizeCheck = validateOfficialSizeChartDraft({
          required: true,
          mode: 'create',
          sizeClass: draft.sizeClass,
          records: draft.records,
          groupList: draft.groupList,
          elementList: draft.elementList,
        });
        if (!sizeCheck.ok) {
          showToast(sizeCheck.message || chartText('publishExtra.fillComplete', '请先完整填写尺寸信息'), 'error');
          return;
        }

        let platformBusinessIds = draft.selectedBusinessIds
          .map(Number)
          .filter((id) => id > 0);

        // 同步到 Temu 平台尺码表库，便于发品时直接引用 businessId
        if (shopId && !platformBusinessIds.length && draft.mode === 'create') {
          try {
            const saved = await saveOfficialSizeChartAsReusableTemplate({
              shopId,
              catId: categoryId,
              sizeClass: draft.sizeClass,
              chartName: title,
              records: draft.records,
              groupList: draft.groupList,
              elementList: draft.elementList,
              api: temuPlatformAPI,
            });
            if (saved?.businessId) {
              platformBusinessIds = [Number(saved.businessId)];
              draft = {
                ...draft,
                mode: 'template',
                selectedBusinessIds: platformBusinessIds,
                selectedLibraryId: String(saved.businessId),
              };
            }
          } catch (syncErr) {
            // 平台同步失败仍写入站内尺码库，避免「保存成功但尺码库无数据」
            showToast(
              syncErr?.message || chartText('saveTpl.syncFail', '同步平台尺码表库失败，将仅保存到站内尺码库'),
              'warning',
            );
          }
        }

        payload = buildOfficialSizeChartTemplatePayload({
          categoryId,
          productTypeId,
          categoryPathLabel,
          selectedSizeLabels,
          selectedSizeIds,
          sizeClass: draft.sizeClass,
          officialSizeChart: draft,
          platformBusinessIds,
        });
      } else {
        payload = buildSizeChartTemplatePayload({
          sizeChart,
          chartRows,
          measureAttrs,
          sizeSaleAttributeId,
          productTypeId,
          categoryId,
          categoryPathLabel,
        });
        if (!payload.sizeChart || !Object.keys(payload.sizeChart).length) {
          showToast(chartText('saveTpl.emptyChart', '当前尺码表为空，无法保存'), 'error');
          return;
        }
        const sizeCheck = validateSizeChartForm({
          sizeChart,
          measureAttrs,
          sizeAttr,
          selectedSizeIds,
          skcList,
        });
        if (!sizeCheck.ok) {
          showToast(sizeCheck.message || chartText('saveTpl.chartInvalid', '尺码表不符合平台填写要求'), 'error');
          return;
        }
      }

      const body = {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        productTypeId,
        categoryId,
        categoryPathLabel: categoryPathLabel || undefined,
        sizeSaleAttributeId: sizeSaleAttributeId || undefined,
        autoRuleEnabled,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        payload,
      };
      if (officialMode) {
        body.shopType = shopType;
        await adminAPI.createOfficialTemuSizeChartTemplate(body);
      } else {
        await temuPlatformAPI.createSizeChartTemplate(shopId, body);
      }
      showToast(officialMode ? chartText('saveTpl.savedOfficialSize', '已保存到官方尺码库') : chartText('saveTpl.savedSize', '已保存到尺码库'), 'success');
      onSaved?.({ name: title, styleNo: styleNoValue, payload });
      onClose?.();
    } catch (e) {
      showToast(e?.message || chartText('saveTpl.saveSizeFail', '保存到尺码库失败'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-attr-tpl-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel tpo-attr-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-size-chart-save-meta-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-size-chart-save-meta-title">{officialMode ? chartText('saveTpl.saveOfficialSize', fixedUi("保存到官方尺码库")) : chartText('saveTpl.saveAsSize', fixedUi("保存为尺码模板"))}</h2>
          <button type="button" className="tpo-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={saving} onClick={onClose}>×</button>
        </div>
        <div className="tpo-batch-modal__body">
          <div className="tpo-wizard__form tpo-template-create-form">
            <p className="tpo-wizard__inline-hint">
              {chartText('saveTpl.hintSync', '将当前填写的尺寸保存到站内尺码库（含款号与自动规则），下次可从下拉选用；同时会同步到平台尺码表库。')}
            </p>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
              <input
                className="tpo-input"
                value={styleNo}
                disabled={saving}
                maxLength={128}
                placeholder={chartText('saveTpl.stylePh', fixedUi("请输入款号"))}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{chartText('productTemplate.colName', fixedUi("模板名称"))} <RequiredMark /></span>
              <input
                className="tpo-input"
                value={name}
                disabled={saving}
                maxLength={128}
                placeholder={chartText('saveTpl.namePhSize', '如：女装基础尺码表')}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
              <input
                className="tpo-input"
                value={remark}
                disabled={saving}
                maxLength={200}
                placeholder={chartText('publishSpec.patternOptional', fixedUi("可选"))}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <div className="tpo-field tpo-field--full tpo-template-auto-rule">
              <div className="tpo-template-auto-rule__switch">
                <TemuSwitch
                  checked={autoRuleEnabled}
                  disabled={saving}
                  ariaLabel={chartText('productTemplate.colAutoRule', '自动规则')}
                  onChange={setAutoRuleEnabled}
                />
                <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
              </div>
              <p className="tpo-wizard__inline-hint">
                {chartText('saveTpl.autoHint', '开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先。')}
              </p>
              <label className="tpo-field">
                <span className="tpo-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                <input
                  className="tpo-input"
                  type="number"
                  step={1}
                  value={sortOrder}
                  disabled={saving || priorityLoading}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{chartText('photoLib.categoryApply', fixedUi("适用类目"))}</span>
              <input
                className="tpo-input"
                value={categoryPathLabel || categoryId || '—'}
                disabled
                readOnly
              />
            </label>
          </div>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" disabled={saving} onClick={onClose}>
            {chartText('shop.cancel', '取消')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            disabled={saving || (!officialMode && !shopId)}
            onClick={() => void submit()}
          >
            {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : chartText('shop.save', fixedUi("保存"))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
